import { Component, OnInit } from '@angular/core';
import { HttpService } from 'src/app/http.service';

@Component({
  selector: 'app-chat-channel-list',
  templateUrl: './chat-channel-list.component.html',
  styleUrls: ['./chat-channel-list.component.scss']
})
export class ChatChannelListComponent implements OnInit {

  public company_uuid: string = '';
  public channels: any[] = [];
  public loading: boolean = false;

  // MultiChat Server selection
  public servers: any[] = [];
  public selectedServerId: number | null = null;

  // Telegram add form
  public showTelegramForm: boolean = false;
  public telegramToken: string = '';
  public telegramName: string = 'Telegram';
  public telegramConnecting: boolean = false;

  // Telegram Web add form (multi-step auth)
  public showTelegramWebForm: boolean = false;
  public tgWebApiId: string = '';
  public tgWebApiHash: string = '';
  public tgWebPhone: string = '';
  public tgWebName: string = 'Telegram Web';
  public tgWebCode: string = '';
  public tgWebPassword: string = '';
  public tgWebChannelId: number | null = null;
  public tgWebAuthState: string = ''; // '', 'awaiting_phone', 'awaiting_code', 'awaiting_password', 'connected'
  public tgWebConnecting: boolean = false;
  public tgWebUser: any = null;

  constructor(private http: HttpService) {
    this.company_uuid = this.http.getStaticSelectedCompany();
  }

  ngOnInit(): void {
    this.loadChannels();
    this.loadServers();
  }

  async loadChannels() {
    this.loading = true;
    const rtn: any = await this.http.post('/admin/chat/channel/list');
    this.channels = rtn?.channels || [];
    this.loading = false;
  }

  async loadServers() {
    const rtn: any = await this.http.post('/admin/multichat-server/list');
    this.servers = (rtn?.servers || []).filter((s: any) => s.status === 'active');
  }

  hasActiveServers(): boolean {
    return this.servers.length > 0;
  }

  async initWhatsApp(channel_id: number | null = null) {
    if (!channel_id && !this.selectedServerId) {
      this.http.notify('Please select a MultiChat Server first');
      return;
    }
    const payload: any = { config: {} };
    if (channel_id) {
      payload.channel_id = channel_id;
    } else {
      payload.multichat_server_id = this.selectedServerId;
    }
    const rtn: any = await this.http.post('/admin/chat/whatsapp/init', payload);
    this.http.notify(rtn?.message || 'Initializing...');
    await this.loadChannels();
  }

  async disconnectChannel(ch: any) {
    if (!confirm('Disconnect this channel?')) return;
    const rtn: any = await this.http.post('/admin/chat/' + ch.channel + '/disconnect', { channel_id: ch.id });
    this.http.notify(rtn?.message || 'Disconnected');
    await this.loadChannels();
  }

  // ── Telegram ──

  async connectTelegram(channel_id: number | null = null) {
    if (channel_id) {
      // Re-connect existing channel
      this.telegramConnecting = true;
      const rtn: any = await this.http.post('/admin/chat/telegram/init', { channel_id });
      this.telegramConnecting = false;
      this.http.notify(rtn?.message || (rtn?.status ? 'Connected' : 'Failed'));
      await this.loadChannels();
      return;
    }
    // Show form for new bot
    if (!this.selectedServerId) {
      this.http.notify('Please select a MultiChat Server first');
      return;
    }
    this.showTelegramForm = true;
    this.telegramToken = '';
    this.telegramName = 'Telegram';
  }

  async submitTelegram() {
    if (!this.telegramToken.trim()) return;
    if (!this.selectedServerId) {
      this.http.notify('Please select a MultiChat Server first');
      return;
    }
    this.telegramConnecting = true;
    const rtn: any = await this.http.post('/admin/chat/telegram/init', {
      token: this.telegramToken.trim(),
      name: this.telegramName.trim() || 'Telegram',
      multichat_server_id: this.selectedServerId
    });
    this.telegramConnecting = false;

    if (rtn?.status) {
      this.http.notify(rtn.bot ? `Connected @${rtn.bot.username}` : 'Telegram bot connected');
      this.showTelegramForm = false;
      this.telegramToken = '';
      await this.loadChannels();
    } else {
      this.http.notify(rtn?.message || 'Failed to connect');
    }
  }

  cancelTelegramForm() {
    this.showTelegramForm = false;
    this.telegramToken = '';
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'connected': return 'bg-success';
      case 'qr_needed': return 'bg-warning text-dark';
      case 'disconnected': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  getChannelIcon(channel: string): string {
    switch (channel) {
      case 'whatsapp': return '📱';
      case 'telegram': return '🤖';
      case 'telegram_web': return '✈️';
      case 'viber': return '💬';
      case 'wechat': return '💚';
      default: return '🌐';
    }
  }

  // ── Inline Edit ──
  public editingId: number | null = null;
  public editingName: string = '';

  startEdit(ch: any) {
    this.editingId = ch.id;
    this.editingName = ch.name;
  }

  cancelEdit() {
    this.editingId = null;
    this.editingName = '';
  }

  async saveEdit(ch: any) {
    if (!this.editingName.trim()) return;

    const rtn: any = await this.http.post('/admin/chat/channel/update', {
      id: ch.id,
      name: this.editingName.trim()
    });

    if (rtn?.status) {
      ch.name = this.editingName.trim();
      this.http.notify('Channel name updated');
    } else {
      this.http.notify(rtn?.message || 'Failed to update');
    }

    this.editingId = null;
    this.editingName = '';
  }

  async deleteChannel(ch: any) {
    if (!confirm(`Delete channel "${ch.name}"? This will disconnect and remove it permanently.`)) return;

    const rtn: any = await this.http.post('/admin/chat/channel/delete', { id: ch.id });
    if (rtn?.status) {
      this.channels = this.channels.filter((c: any) => c.id !== ch.id);
      this.http.notify('Channel deleted');
    } else {
      this.http.notify(rtn?.message || 'Failed to delete');
    }
  }

  // ── Telegram Web (User Account) ──

  connectTelegramWeb(channel_id: number | null = null) {
    if (channel_id) {
      this.reconnectTelegramWeb(channel_id);
      return;
    }
    if (!this.selectedServerId) {
      this.http.notify('Please select a MultiChat Server first');
      return;
    }
    this.showTelegramWebForm = true;
    this.tgWebApiId = '';
    this.tgWebApiHash = '';
    this.tgWebPhone = '';
    this.tgWebName = 'Telegram Web';
    this.tgWebCode = '';
    this.tgWebPassword = '';
    this.tgWebChannelId = null;
    this.tgWebAuthState = '';
    this.tgWebUser = null;
  }

  async reconnectTelegramWeb(channel_id: number) {
    this.tgWebConnecting = true;
    const rtn: any = await this.http.post('/admin/chat/telegram-web/init', { channel_id });
    this.tgWebConnecting = false;
    if (rtn?.auth_state === 'connected') {
      this.http.notify('Telegram Web reconnected');
    } else if (rtn?.status) {
      // Needs re-auth — show form in code step
      this.showTelegramWebForm = true;
      this.tgWebChannelId = channel_id;
      this.tgWebAuthState = rtn.auth_state || 'awaiting_phone';
    } else {
      this.http.notify(rtn?.message || 'Failed');
    }
    await this.loadChannels();
  }

  async submitTelegramWebInit() {
    if (!this.tgWebApiId.trim() || !this.tgWebApiHash.trim()) return;
    if (!this.selectedServerId) {
      this.http.notify('Please select a MultiChat Server first');
      return;
    }
    this.tgWebConnecting = true;
    const rtn: any = await this.http.post('/admin/chat/telegram-web/init', {
      api_id: this.tgWebApiId.trim(),
      api_hash: this.tgWebApiHash.trim(),
      name: this.tgWebName.trim() || 'Telegram Web',
      multichat_server_id: this.selectedServerId
    });
    this.tgWebConnecting = false;

    if (rtn?.status) {
      this.tgWebChannelId = rtn.channel_id;
      this.tgWebAuthState = rtn.auth_state || 'awaiting_phone';
      if (rtn.auth_state === 'connected') {
        this.tgWebUser = rtn.user;
        this.http.notify('Connected as ' + (rtn.user?.firstName || 'user'));
        this.cancelTelegramWebForm();
        await this.loadChannels();
      }
    } else {
      this.http.notify(rtn?.message || 'Failed');
    }
  }

  async submitTelegramWebPhone() {
    if (!this.tgWebPhone.trim() || !this.tgWebChannelId) return;
    this.tgWebConnecting = true;
    const rtn: any = await this.http.post('/admin/chat/telegram-web/send-code', {
      channel_id: this.tgWebChannelId,
      phone: this.tgWebPhone.trim()
    });
    this.tgWebConnecting = false;

    if (rtn?.status) {
      this.tgWebAuthState = 'awaiting_code';
      this.http.notify(rtn.message || 'Code sent');
    } else {
      this.http.notify(rtn?.message || 'Failed to send code');
    }
  }

  async submitTelegramWebCode() {
    if (!this.tgWebCode.trim() || !this.tgWebChannelId) return;
    this.tgWebConnecting = true;
    const rtn: any = await this.http.post('/admin/chat/telegram-web/verify-code', {
      channel_id: this.tgWebChannelId,
      code: this.tgWebCode.trim()
    });
    this.tgWebConnecting = false;

    if (rtn?.status) {
      if (rtn.auth_state === 'awaiting_password') {
        this.tgWebAuthState = 'awaiting_password';
        this.http.notify('2FA password required');
      } else {
        this.tgWebUser = rtn.user;
        this.http.notify('Authenticated as ' + (rtn.user?.firstName || 'user'));
        this.cancelTelegramWebForm();
        await this.loadChannels();
      }
    } else {
      this.http.notify(rtn?.message || 'Invalid code');
    }
  }

  async submitTelegramWebPassword() {
    if (!this.tgWebPassword.trim() || !this.tgWebChannelId) return;
    this.tgWebConnecting = true;
    const rtn: any = await this.http.post('/admin/chat/telegram-web/verify-password', {
      channel_id: this.tgWebChannelId,
      password: this.tgWebPassword.trim()
    });
    this.tgWebConnecting = false;

    if (rtn?.status) {
      this.tgWebUser = rtn.user;
      this.http.notify('Authenticated as ' + (rtn.user?.firstName || 'user'));
      this.cancelTelegramWebForm();
      await this.loadChannels();
    } else {
      this.http.notify(rtn?.message || 'Wrong password');
    }
  }

  cancelTelegramWebForm() {
    this.showTelegramWebForm = false;
    this.tgWebApiId = '';
    this.tgWebApiHash = '';
    this.tgWebPhone = '';
    this.tgWebCode = '';
    this.tgWebPassword = '';
    this.tgWebChannelId = null;
    this.tgWebAuthState = '';
    this.tgWebUser = null;
  }
}
